import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Alert,
  Snackbar,
  CircularProgress,
  Stack,
  Box,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import BlockIcon from "@mui/icons-material/Block";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "./firebase-config";
import dayjs from "dayjs";

const docRef = doc(db, "system", "attendanceDates");

const AttendanceDates = () => {
  const [allowedDates, setAllowedDates] = useState([]);
  const [blockedDates, setBlockedDates] = useState([]);
  const [loading, setLoading] = useState(true);

  // Date picker state
  const [allowedDateInput, setAllowedDateInput] = useState(dayjs());
  const [blockedDateInput, setBlockedDateInput] = useState(dayjs());

  // Feedback notifications
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const showNotification = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  useEffect(() => {
    const fetchDates = async () => {
      setLoading(true);
      try {
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          setAllowedDates(data.allowedDates || []);
          setBlockedDates(data.blockedDates || []);
        } else {
          // Initialize empty document
          await setDoc(docRef, { allowedDates: [], blockedDates: [] });
          setAllowedDates([]);
          setBlockedDates([]);
        }
      } catch (error) {
        console.error("Error fetching attendance dates settings:", error);
        showNotification("Failed to load attendance dates from database.", "error");
      } finally {
        setLoading(false);
      }
    };
    fetchDates();
  }, []);

  const saveToFirestore = async (newAllowed, newBlocked) => {
    try {
      await setDoc(docRef, {
        allowedDates: newAllowed,
        blockedDates: newBlocked,
      });
      return true;
    } catch (error) {
      console.error("Error saving attendance dates:", error);
      showNotification("Failed to save changes to the database.", "error");
      return false;
    }
  };

  const handleAddAllowedDate = async () => {
    if (!allowedDateInput || !allowedDateInput.isValid()) {
      showNotification("Please select a valid date.", "warning");
      return;
    }

    if (allowedDateInput.day() === 0) {
      showNotification("Allowed dates must be non-Sundays. Sundays are allowed by default.", "warning");
      return;
    }

    const dateStr = allowedDateInput.format("YYYY-MM-DD");
    if (allowedDates.includes(dateStr)) {
      showNotification("This date is already in the allowed list.", "warning");
      return;
    }

    const updatedAllowed = [...allowedDates, dateStr].sort();
    const success = await saveToFirestore(updatedAllowed, blockedDates);
    if (success) {
      setAllowedDates(updatedAllowed);
      showNotification(`Added ${allowedDateInput.format("DD-MM-YYYY")} to allowed dates.`);
    }
  };

  const handleAddBlockedDate = async () => {
    if (!blockedDateInput || !blockedDateInput.isValid()) {
      showNotification("Please select a valid date.", "warning");
      return;
    }

    if (blockedDateInput.day() !== 0) {
      showNotification("Blocked dates must be Sundays. Non-Sundays are blocked by default.", "warning");
      return;
    }

    const dateStr = blockedDateInput.format("YYYY-MM-DD");
    if (blockedDates.includes(dateStr)) {
      showNotification("This Sunday is already in the blocked list.", "warning");
      return;
    }

    const updatedBlocked = [...blockedDates, dateStr].sort();
    const success = await saveToFirestore(allowedDates, updatedBlocked);
    if (success) {
      setBlockedDates(updatedBlocked);
      showNotification(`Added Sunday ${blockedDateInput.format("DD-MM-YYYY")} to blocked dates.`);
    }
  };

  const handleDeleteAllowedDate = async (dateToDelete) => {
    const updatedAllowed = allowedDates.filter((date) => date !== dateToDelete);
    const success = await saveToFirestore(updatedAllowed, blockedDates);
    if (success) {
      setAllowedDates(updatedAllowed);
      showNotification(`Removed ${dayjs(dateToDelete).format("DD-MM-YYYY")} from allowed dates.`);
    }
  };

  const handleDeleteBlockedDate = async (dateToDelete) => {
    const updatedBlocked = blockedDates.filter((date) => date !== dateToDelete);
    const success = await saveToFirestore(allowedDates, updatedBlocked);
    if (success) {
      setBlockedDates(updatedBlocked);
      showNotification(`Removed Sunday ${dayjs(dateToDelete).format("DD-MM-YYYY")} from blocked dates.`);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "300px" }}>
        <CircularProgress />
      </Box>
    );
  }

  const isAllowedInputSunday = allowedDateInput && allowedDateInput.day() === 0;
  const isBlockedInputNonSunday = blockedDateInput && blockedDateInput.day() !== 0;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h5" align="center" gutterBottom sx={{ fontWeight: "bold" }}>
          Attendance Dates Configuration
        </Typography>
        <Typography variant="body2" color="textSecondary" align="center" sx={{ mb: 4 }}>
          Manage special dates for attendance submissions. By default, attendance submissions are allowed on Sundays and blocked on non-Sundays.
        </Typography>

        <Grid container spacing={4}>
          {/* Allowed Non-Sundays Column */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
              <CardContent sx={{ flexGrow: 1 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                  <CheckCircleOutlinedIcon color="success" />
                  <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                    Allowed Non-Sundays
                  </Typography>
                </Stack>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                  Add specific weekdays/Saturdays when attendance submission should be permitted (e.g. retreats or festivals).
                </Typography>

                <Stack spacing={2} sx={{ mb: 3 }}>
                  <DatePicker
                    label="Select Allowed Date"
                    value={allowedDateInput}
                    onChange={(newDate) => setAllowedDateInput(newDate)}
                    format="DD-MM-YYYY"
                  />

                  {isAllowedInputSunday && (
                    <Alert severity="warning" size="small">
                      Sundays are allowed by default. Please select a non-Sunday.
                    </Alert>
                  )}

                  <Button
                    variant="contained"
                    color="success"
                    onClick={handleAddAllowedDate}
                    disabled={isAllowedInputSunday || !allowedDateInput}
                    startIcon={<CalendarTodayIcon />}
                  >
                    Allow Date
                  </Button>
                </Stack>

                <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1 }}>
                  Currently Allowed ({allowedDates.length})
                </Typography>
                {allowedDates.length === 0 ? (
                  <Typography variant="body2" color="textSecondary" sx={{ fontStyle: "italic" }}>
                    No special non-Sundays configured.
                  </Typography>
                ) : (
                  <List dense sx={{ maxHeight: 250, overflow: "auto" }}>
                    {allowedDates.map((dateStr) => {
                      const dateObj = dayjs(dateStr);
                      return (
                        <ListItem key={dateStr} divider>
                          <ListItemText
                            primary={dateObj.format("DD-MM-YYYY")}
                            secondary={dateObj.format("dddd")}
                          />
                          <ListItemSecondaryAction>
                            <IconButton
                              edge="end"
                              aria-label="delete"
                              onClick={() => handleDeleteAllowedDate(dateStr)}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      );
                    })}
                  </List>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Blocked Sundays Column */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
              <CardContent sx={{ flexGrow: 1 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                  <BlockIcon color="error" />
                  <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                    Blocked Sundays
                  </Typography>
                </Stack>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                  Add specific Sunday dates when attendance submission should be blocked (e.g. during school holidays).
                </Typography>

                <Stack spacing={2} sx={{ mb: 3 }}>
                  <DatePicker
                    label="Select Blocked Sunday"
                    value={blockedDateInput}
                    onChange={(newDate) => setBlockedDateInput(newDate)}
                    format="DD-MM-YYYY"
                  />

                  {isBlockedInputNonSunday && (
                    <Alert severity="warning" size="small">
                      Non-Sundays are blocked by default. Please select a Sunday.
                    </Alert>
                  )}

                  <Button
                    variant="contained"
                    color="error"
                    onClick={handleAddBlockedDate}
                    disabled={isBlockedInputNonSunday || !blockedDateInput}
                    startIcon={<BlockIcon />}
                  >
                    Block Sunday
                  </Button>
                </Stack>

                <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1 }}>
                  Currently Blocked ({blockedDates.length})
                </Typography>
                {blockedDates.length === 0 ? (
                  <Typography variant="body2" color="textSecondary" sx={{ fontStyle: "italic" }}>
                    No Sundays are blocked.
                  </Typography>
                ) : (
                  <List dense sx={{ maxHeight: 250, overflow: "auto" }}>
                    {blockedDates.map((dateStr) => {
                      const dateObj = dayjs(dateStr);
                      return (
                        <ListItem key={dateStr} divider>
                          <ListItemText
                            primary={dateObj.format("DD-MM-YYYY")}
                            secondary={dateObj.format("dddd")}
                          />
                          <ListItemSecondaryAction>
                            <IconButton
                              edge="end"
                              aria-label="delete"
                              onClick={() => handleDeleteBlockedDate(dateStr)}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      );
                    })}
                  </List>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={4000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: "100%" }}>
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Container>
    </LocalizationProvider>
  );
};

export default AttendanceDates;
